import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons from '@fortawesome/free-regular-svg-icons';
import * as icons_solid from '@fortawesome/free-solid-svg-icons';
import { generateClient } from '@aws-amplify/api';
import { getSubjects } from '@/graphql/small-queries';
import { getDistinctSubjectStatuses } from '@/graphql/queries';
import { upsertBulkSubjects, upsertSubject } from '@/graphql/mutations';
import Breadcrumb from '@/Breadcrumb';
import ErrorModal from '@/ErrorModal';
import SubjectTimeline from './SubjectTimeline';
import Dashboard from '@/Dashboard';
import TranscriptModal from '@/TranscriptModal';
import MergeModal from './MergeModal';
import useBodyClick from '@/hooks/useBodyClick';
import useArrowKeys from '@/hooks/useArrowKeys';
import useLoadMore from '@/hooks/useLoadMore';
import useSubscription from '@/hooks/useSubscription';
import Header from '@/Header';
import { dash, downIcon, outcome_color, SpinnerLineDark, upIcon } from '@/utils';
import { outcomes as _outcomes } from '@/WorkflowEntry/const';
import { getListSort, setListSort } from '@/utils/stickySettings';

const recordingIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faVolumeLow} size="lg" />);
const transcriptIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faQuoteLeft} size="lg" />);
const possibleDupeIcon = (<FontAwesomeIcon className="fg-purple cursor-pointer" icon={icons_solid.faUserGroup} size="md" title="Possible Duplicate" />);

const client = generateClient();

const dt = value => {
  return {
    long: value ? moment(value).format('LLL') : undefined,
    medium: value ? moment(value).format('M/D h:mm A') : undefined,
    relative: value ? moment(value).fromNow() : undefined,
    short: value ? moment(value).format('M/D/YYYY') : undefined
  };
};

const is_phone = term => /^[+\d\s().-]*(?:\s*(?:x|ext\.?)\s*\d+)?$/i.test(term.trim());

export default ({ defaultEnrollmentStatus, links = [], path = '/app/pipeline/subjects/', showDashboard = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState({});
  const updateBusy = useCallback((id, value) => {
    setBusy(prevState => ({ ...prevState, [id]: value }));
  }, []);
  const showCCID = useRef(false);
  const showCRID = useRef(false);
  const showStudyStatus = useRef(false);
  const showStatus = useRef(false);
  const [outcomes, setOutcomes] = useState();
  const [statuses, setStatuses] = useState();

  const [distinctStatuses, setDistinctStatuses] = useState();
  const loadDistinctStatuses = useCallback(() => {
    return client.graphql({ query: getDistinctSubjectStatuses })
      .then(res => setDistinctStatuses(res.data.getDistinctSubjectStatuses.statuses.map(status => ({ status }))));
  }, []);

  const queryParams = new URLSearchParams(location.search);
  const deal_id = queryParams.get('deal_id') || undefined;
  const deal_status = queryParams.get('deal_status') || undefined;
  const enrollment_status = queryParams.get('enrollment_status') || defaultEnrollmentStatus;
  const next_task_id = queryParams.get('next_task_id') || undefined;
  const next_touchpoint = queryParams.get('next_touchpoint') || undefined;
  const protocol_id = queryParams.get('protocol_id') || undefined;
  const questionnaire_id = parseInt(queryParams.get('questionnaire_id') || undefined, 10);
  const recruitment_id = parseInt(queryParams.get('recruitment_id') || undefined, 10);
  const segment_id = parseInt(queryParams.get('segment_id') || undefined, 10);
  const site_id = parseInt(queryParams.get('site_id') || undefined, 10);
  const study_id = parseInt(queryParams.get('study_id') || undefined, 10);
  const workflow_id = parseInt(queryParams.get('workflow_id') || undefined, 10);
  const filter_fields = [deal_id, deal_status, enrollment_status, next_task_id, next_touchpoint, protocol_id, questionnaire_id, recruitment_id, segment_id, site_id, study_id, workflow_id];

  const [search, setSearch] = useState("");
  const [searchCount, setSearchCount] = useState(0);
  const incSearchCount = useCallback(() => setSearchCount((p) => p + 1), []);
  const debounceSearch = useCallback(_.debounce((v) => { setDefaults(); setSearch(v) }, 500), []);

  const { field: defaultSortField, order: defaultSortOrder } = useMemo(() => getListSort({ key: window.location.pathname }) || { field: 'updated_at', order: 'desc' }, []);
  const [sortField, setSortField] = useState(defaultSortField && defaultSortOrder ? defaultSortField : undefined);
  const [sortOrder, setSortOrder] = useState({
    // Some default directions
    last_contact_started_at: 'desc',
    lead_created_at: 'desc',
    updated_at: 'desc',
    ...(defaultSortField && defaultSortOrder ? { [defaultSortField]: defaultSortOrder } : undefined)
  });

  const filters_ref = useRef();
  filters_ref.current = useMemo(() => {
    const filters = [];
    const q = (search || '').trim();
    if (q) {
      const n = Number(q);
      filters.push(...[
        // Test number fields only if search conforms to a Number
        ...(!isNaN(n) ? [
          { ccid: { eq: n } },
          { crid: { eq: n } },
          { id: { eq: n } }
        ] : []),
        { full_name_first: { like: `%${q}%` } },
        ...(q.indexOf('@') !== -1 ? [{ subject_emails: [{ email_address: { like: `%${q}%` } }] }] : []),
        ...(is_phone(q) ? [
          { subject_phone_numbers: [{ phone_number: { like: `%${q}%` } }] }
        ]: [])
      ]);
    }
    const queryParams = new URLSearchParams(window.location.search);
    const deal_id = queryParams.get('deal_id') || undefined;
    const deal_status = queryParams.get('deal_status') || undefined;
    const enrollment_status = queryParams.get('enrollment_status') || defaultEnrollmentStatus;
    const next_task_id = queryParams.get('next_task_id') || undefined;
    const next_touchpoint = queryParams.get('next_touchpoint') || undefined;
    const protocol_id = queryParams.get('protocol_id') || undefined;
    const questionnaire_id = parseInt(queryParams.get('questionnaire_id') || undefined, 10);
    const questionnaire_outcome = queryParams.get('questionnaire_outcome') || undefined;
    const recruitment_id = parseInt(queryParams.get('recruitment_id') || undefined, 10);
    const segment_id = parseInt(queryParams.get('segment_id') || undefined, 10);
    const site_id = parseInt(queryParams.get('site_id') || undefined, 10);
    const study_id = parseInt(queryParams.get('study_id') || undefined, 10);
    const workflow_id = parseInt(queryParams.get('workflow_id') || undefined, 10);
    const _filters = {
      ...(deal_id ? { deal_id } : undefined),
      ...(deal_status ? { deal_status } : undefined),
      ...(enrollment_status ? { enrollment_status } : undefined),
      ...(next_task_id ? { next_task_id } : undefined),
      ...(next_touchpoint ? { next_touchpoint } : undefined),
      ...(outcomes?.length > 0 ? { contact_outcomes: outcomes } : undefined),
      ...(protocol_id ? { protocol_id } : undefined),
      ...(questionnaire_id ? { questionnaire_id } : undefined),
      ...(questionnaire_outcome ? { questionnaire_outcome } : undefined),
      ...(recruitment_id ? { recruitment_id } : undefined),
      ...(segment_id ? { segment_id } : undefined),
      ...(site_id ? { site_id } : undefined),
      ...(statuses?.length > 0 ? { statuses: statuses.map(e => e.status) } : undefined),
      ...(study_id ? { study_id } : undefined),
      ...(workflow_id ? { workflow_id } : undefined)
    };
    const top_filters = _.cloneDeep(_filters);
    if ('study_id' in top_filters && 'protocol_id' in top_filters) delete top_filters.study_id;
    let parts = (enrollment_status || '').split('::');
    if (parts.length > 1 && parts[1] === 'Pending') {
      top_filters.enrollment_status_raw = parts[0];
      top_filters.enrollment_status = undefined;
    }
    parts = (deal_status || '').split('::');
    if (parts.length > 1 && parts[1] === 'Pending') {
      top_filters.deal_status_raw = parts[0];
      top_filters.deal_status = undefined;
    }
    const sorting = [
      { field: sortField, order: sortOrder[sortField] },
      { field: 'id', order: 'asc' }
    ];
    return {
      filters,
      sorting,
      top_filters
    };
  }, [...filter_fields, outcomes, search, sortField, sortOrder, statuses]);

  const { isLoadingMore, limit, loadMoreButton, offset, rows, setDefaults, setIsLoadingMore, setRows, setTotal, total } = useLoadMore();
  useSubscription({ filters_ref, limit_ref: limit, offset_ref: offset, rankings_key: 'subjects', setRows });

  // -------- Bulk selection (minimal, no restyle) --------
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const selectedCount = selectedIds.size;

  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelected = useCallback(() => setSelectedIds(new Set()), []);

  const allRowIds = useMemo(() => (rows || []).map((r) => r.id), [rows]);

  const allSelectedOnPage = useMemo(() => {
    if (!allRowIds.length) return false;
    return allRowIds.every((id) => selectedIds.has(id));
  }, [allRowIds, selectedIds]);

  const someSelectedOnPage = useMemo(() => {
    if (!allRowIds.length) return false;
    return allRowIds.some((id) => selectedIds.has(id));
  }, [allRowIds, selectedIds]);

  const toggleSelectAllOnPage = useCallback(() => {
    // reset shift anchor (avoids surprising range behavior after select-all)
    lastSelectedIndexRef.current = null;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      const shouldSelectAll = !allRowIds.every((id) => next.has(id));
      if (shouldSelectAll) allRowIds.forEach((id) => next.add(id));
      else allRowIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [allRowIds]);

  // ✅ Shift+click range selection support (robust)
  const lastSelectedIndexRef = useRef(null);

  const handleRowCheckboxClick = useCallback((id, index) => (e) => {
    e.stopPropagation();
    // Save a copy
    const _lastSelectedIndex = lastSelectedIndexRef.current;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const alreadySelected = prev.has(id);
      const shouldSelect = !alreadySelected; // what this click intends
      if (e.shiftKey && _lastSelectedIndex !== null) {
        const start = Math.min(_lastSelectedIndex, index);
        const end = Math.max(_lastSelectedIndex, index);
        const idsInRange = allRowIds.slice(start, end + 1);
        if (shouldSelect) idsInRange.forEach((rid) => next.add(rid));
        else idsInRange.forEach((rid) => next.delete(rid));
      } else {
        if (shouldSelect) next.add(id);
        else next.delete(id);
      }
      return next;
    });
    lastSelectedIndexRef.current = index;
  }, [allRowIds]);

  const [sidebarItem, _setSidebarItem] = useState();
  const setSidebarItem = useCallback(item => {
    _setSidebarItem(item);
    const queryParams = new URLSearchParams(window.location.search);
    if (!item) {
      queryParams.delete('subject_id');
    } else {
      queryParams.set('subject_id', item.id);
    }
    navigate({ pathname: window.location.pathname, search: `?${queryParams.toString()}` }, { replace: true });
  }, []);

  const [loadCount, setLoadCount] = useState(0);
  const incLoadCount = () => setLoadCount(prevState => prevState + 1);
  const [title, setTitle] = useState({ label: undefined, totalRows: undefined });
  const [modal, setModal] = useState();
  const [error, setError] = useState();

  const [workflowModal, setWorkflowModal] = useState(null);
  const [workflowOptions, setWorkflowOptions] = useState([]);
  const [workflowOptionsLoading, setWorkflowOptionsLoading] = useState(false);
  const [workflowOptionsError, setWorkflowOptionsError] = useState(null);

  const loadWorkflowOptions = useCallback(async () => {
    setWorkflowOptionsLoading(true);
    setWorkflowOptionsError(null);
    try {
      const res = await client.graphql({
        query: 'query GetWorkflows { getWorkflows(sorting: { field: name, order: asc }) { workflows { id name } } }'
      });

      const items = res?.data?.getWorkflows?.workflows ?? []; // adjust
      setWorkflowOptions(items.map(w => ({
        value: String(w.id),
        label: w.name ? `${w.name}` : `Workflow ${w.id}`,
      })));
    } catch (e) {
      console.error(e);
      setWorkflowOptionsError('Failed to load workflows');
      setWorkflowOptions([]);
    } finally {
      setWorkflowOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!workflowModal) return;
    console.error('here');
    loadWorkflowOptions();
  }, [workflowModal, loadWorkflowOptions]);

  // React-controlled bulk menu (no Bootstrap JS required)
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const bulkMenuRef = useRef(null);

  useEffect(() => {
    if (!bulkMenuOpen) return;

    const onDocClick = (e) => {
      if (bulkMenuRef.current && bulkMenuRef.current.contains(e.target)) return;
      setBulkMenuOpen(false);
    };

    // capture helps when other document click handlers are present (e.g., useBodyClick)
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, [bulkMenuOpen]);

  const catchError = error => {
    if (error instanceof Error) {
      setError(error.toString());
    } else if (typeof error === 'string') {
      setError(error);
    } else {
      setError((error.errors || []).map(e => e.message?.toString()));
    }
  };

  useEffect(() => {
    setDefaults();
  }, [...filter_fields, total]);

  useEffect(() => {
    const subject_id = parseInt(queryParams.get('subject_id') || undefined, 10);
    if (subject_id) {
      const item = rows.find(e => e.id === subject_id);
      if (item) setSidebarItem(item);
    }
  }, [rows]);

  // ---------- load from GraphQL ----------
  const load = useCallback(async () => {
    const { filters, sorting, top_filters } = filters_ref.current;

    const res = await client.graphql({
      query: getSubjects,
      variables: { ...top_filters, filters, limit, offset, sorting, subject_contacts_limit: 1 }
    });

    const payload = res?.data?.getSubjects;
    const data = payload?.subjects ?? [];
    setRows(prevState => {
      const _data = (offset === 0 ? data : [...prevState, ...data]);
      return _data;
    });
    const total = payload?.total_count ?? data.length;
    setTotal(total);
    setTitle({ label: enrollment_status || deal_status, total });
    incLoadCount();
    setIsLoadingMore(false);
  }, [filters_ref.current, limit, offset]);

  useEffect(() => {
    if (loadCount > 0) setSidebarItem();
    load().catch(console.error);
  }, [load]);

  useBodyClick({ onClick: setSidebarItem });
  useArrowKeys({ rows, setSidebarRow: setSidebarItem, sidebarRow: sidebarItem });

  const handleDelete = useCallback(async (row) => {
    const ok = window.confirm(`Delete subject "${row.full_name_last ?? row.id}"?`);
    if (!ok) return;
    try {
      updateBusy(row.id, true);
      if (row.id === sidebarItem?.id) {
        setSidebarItem();
      }
      await client.graphql({
        query: upsertSubject,
        variables: {
          input: { subjects: { id: row.id } },
          input_delete: [{ id: row.id, key: "subjects" }],
        },
      });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setTotal((t) => Math.max(0, t - 1));

      setSelectedIds((prev) => {
        if (!prev.has(row.id)) return prev;
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    } catch (e) {
      console.error(e);
      alert("Delete failed");
    } finally {
      updateBusy(row.id, false);
    }
  }, [sidebarItem]);

  const handleBulkAddWorkflow = useCallback(async (workflow_id_to_add) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;

    try {
      ids.forEach(id => updateBusy(id, true));
      const input = [];
      ids.forEach(id => {
        input.push({
          input: {
            subjects: { id },
            subject_workflows: [{ workflow_id: workflow_id_to_add }]
          }
        });
      });
      console.log('input', input);
      await client.graphql({
        query: upsertBulkSubjects,
        variables: {
          input: {
            upsertSubjects: input
          }
        }
      });
      clearSelected();
    } catch (e) {
      console.error(e);
      alert("Bulk add workflow failed");
    } finally {
      ids.forEach((id) => updateBusy(id, false));
    }
  }, [selectedIds, clearSelected]);

  const handleSort = field => () => {
    if (sortField === field) {
      setSortOrder(prevState => {
        const order = prevState[sortField] === 'asc' ? 'desc' : 'asc';
        setListSort({ field, key: window.location.pathname, order });
        return ({ ...prevState, [field]: order });
      });
    } else {
      setSortOrder(prevState => {
        const order = prevState[field] || 'asc';
        setListSort({ field, key: window.location.pathname, order });
        return ({ ...prevState, [field]: order });
      });
      setSortField(field);
    }
    setDefaults();
  };

  const sortIcon = sortOrder[sortField] === 'asc' ? upIcon : downIcon;

  const stickyThStyle = {
    position: "sticky",
    top: 0,
    backgroundColor: "#F4F4F4",
    zIndex: 2,
    fontSize: "0.875rem",
    textAlign: "left",
  };

  const idCellStyle        = { borderRight: "1px solid #ccc", padding: "0 12px", maxWidth: 90  };
  const ccidCellStyle      = { borderRight: "1px solid #ccc", padding: "0 12px", maxWidth: 100 };
  const textCellStyle      = { borderRight: "1px solid #ccc", padding: "0 12px", maxWidth: 250 };
  const lastCellStyle      = { padding: "0 12px", maxWidth: 240 };

  const subject_breakout = subject => {
    return (
      <div onClick={event => event.stopPropagation()}>
        <SubjectTimeline catchError={catchError} data={subject} key={subject.id} path={path} setModal={setModal} subject_id={subject.id} />
      </div>
    );
  };

  const table = useMemo(() => {
    // Throw these switches positive only so they stick
    if (rows.find(e => e.ccid)) showCCID.current = true;
    if (rows.find(e => e.crid)) showCRID.current = true;
    if (rows.find(e => e.active_enrollment_status)) showStudyStatus.current = true;
    if (rows.find(e => e.statuses)) showStatus.current = true;
    return (
    <div className="row g-0">
      <div className="col pe-0">
        {/* Table */}
        <div className="rounded-3 overflow-auto" style={{ maxHeight: showDashboard ? 'auto' : 'calc(100vh - 150px)', userSelect: 'none' }}>
          <table
            className="border table table-sm table-borderless align-middle mb-0"
            onClick={event => event.stopPropagation()}
            style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem", tableLayout: "auto" }}
          >
            <thead className="text-muted small" style={{ height: "30px" }}>
              <tr className="border-bottom">
                {/* Bulk select header */}
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3">
                  <input
                    type="checkbox"
                    checked={allSelectedOnPage}
                    ref={(el) => {
                      if (el) el.indeterminate = !allSelectedOnPage && someSelectedOnPage;
                    }}
                    onChange={(e) => { e.stopPropagation(); toggleSelectAllOnPage(); }}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Select all on page"
                  />
                </th>

                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('id')}>ID{sortField === 'id' && sortIcon}</th>
                {showCCID.current && <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('ccid')}>CCID{sortField === 'ccid' && sortIcon}</th>}
                {showCRID.current && <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('crid')}>CRID{sortField === 'crid' && sortIcon}</th>}
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('full_name_last')}>Full Name{sortField === 'full_name_last' && sortIcon}</th>
                {showStudyStatus.current && <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('active_enrollment_status')}>Study Status{sortField === 'active_enrollment_status' && sortIcon}</th>}
                {showStatus.current && <th style={{...stickyThStyle, minWidth: '100px'}} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('statuses')}>Status{sortField === 'statuses' && sortIcon}</th>}
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('last_contact_started_at')}>Last Engagement{sortField === 'last_contact_started_at' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('next_touchpoint')}>Next Touchpoint{sortField === 'next_touchpoint' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('recruitment_source_name')}>Lead Source{sortField === 'recruitment_source_name' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('current_study_name')}>Study{sortField === 'current_study_name' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('lead_created_at')}>Lead Created{sortField === 'lead_created_at' && sortIcon}</th>
                <th style={stickyThStyle} className="text-start align-middle py-2 ps-3 cursor-pointer" onClick={handleSort('updated_at')}>Updated At{sortField === 'updated_at' && sortIcon}</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, index) => {
                const sc = r.subject_contacts?.[0];
                let started_at;
                if (sc?.started_at) started_at = moment(sc.started_at).format('M/DD/YY h:mm A');
                let d;
                if (sc?.duration_seconds) {
                  const _d = moment.duration(sc.duration_seconds, 'seconds');
                  const h = Math.floor(_d.asHours());
                  const m = _d.minutes();
                  const s = _d.seconds();
                  d = `${h > 0 ? String(h).padStart(2, '0') + ':' : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
                }
                const onClose = () => {
                  setModal();
                };
                const onClick = ({ autoplay = false, event } = {}) => {
                  event.stopPropagation();
                  setModal(<TranscriptModal autoplay={autoplay} id={sc.contact_id} onClose={onClose} />);
                };
                const onMergeSuccess = id => {
                  // Immediately remove the source row
                  setRows(prev => prev.filter((r) => r.id !== id));
                  setTotal(t => Math.max(0, t - 1));

                  setSelectedIds((prev) => {
                    if (!prev.has(id)) return prev;
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                  });
                };
                return (
                  <tr
                    className={`position-relative cursor-default border-bottom ${sidebarItem?.id === r.id ? 'bw-focus-ring' : ''}`}
                    key={r.id}
                    onClick={event => { event.stopPropagation(); setSidebarItem(r); }}
                    style={{ height: "30px" }}
                  >
                    {/* Bulk select (UPDATED: whole cell clickable) */}
                    <td
                      className="text-start align-middle py-2 ps-3"
                      style={{ ...idCellStyle, cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Select subject ${r.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowCheckboxClick(r.id, index)(e);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRowCheckboxClick(r.id, index)(e);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected(r.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowCheckboxClick(r.id, index)(e);
                        }}
                        onChange={() => {}}
                        aria-label={`Select subject ${r.id}`}
                      />
                    </td>

                    {/* ID */}
                    <td className="text-start align-middle py-2 ps-3" style={idCellStyle}>
                      <Link
                        to={{ pathname: path + r.id, search: location.search }}
                        className="text-decoration-none"
                        style={{ color: "inherit" }}
                      >
                        {r.id}
                        {busy[r.id] ? (
                          <SpinnerLineDark />
                        ) : undefined}
                      </Link>
                    </td>

                    {/* CCID */}
                    {showCCID.current && (
                      <td className="text-start align-middle py-2 ps-3" style={ccidCellStyle}>
                        {r.ccid ?? dash}
                      </td>
                    )}

                    {/* CRID */}
                    {showCRID.current && (
                      <td className="text-start align-middle py-2 ps-3" style={ccidCellStyle}>
                        {r.crid ?? dash}
                      </td>
                    )}

                    {/* Full Name */}
                    <td
                      className="text-start align-middle py-2 ps-3 text-break"
                      style={textCellStyle}
                      title={r.full_name_last || ""}
                    >
                      <Link
                        to={{ pathname: path + r.id, search: location.search }}
                        className="text-decoration-none fg-purple"
                      >
                        <b>{r.full_name_last || dash}</b>
                      </Link>
                      {r.is_possible_duplicate && <div className="float-end" onClick={event => {
                        event.stopPropagation();
                        setModal(<MergeModal onClose={onClose} onSuccess={() => onMergeSuccess(r.id)} subject_id_from={r.id} />);
                      }}>{possibleDupeIcon}</div>}
                    </td>

                    {/* Study Status */}
                    {showStudyStatus.current && (
                      <td
                        className="text-start align-middle py-2 ps-3 text-break"
                        style={textCellStyle}
                      >
                        {r.active_enrollment_status || dash}
                      </td>
                    )}

                    {/* Status */}
                    {showStatus.current && (
                      <td
                        className="text-start align-middle py-2 ps-3 text-break"
                        style={textCellStyle}
                      >
                        {r.statuses?.length > 0 ? (
                          <div className="">
                            {r.statuses.map((status, i) => <span className={`col-auto mt-1 me-1 p-1 small badge bg-secondary`} key={i}>{status}</span>)}
                          </div>
                        ) : dash}
                      </td>
                    )}

                    {/* Last Engagement */}
                    <td
                      className="text-start align-middle py-2 px-3 text-break"
                      style={textCellStyle}
                      title={r.last_touchpoint || ""}
                    >
                      {sc ? (
                        <>
                          <div className="row g-0">
                            <div className="col"><b>{started_at}</b>&nbsp;{d}</div>
                            {sc?.recording_url && <div className="col-auto ms-2 btn-link cursor-pointer" onClick={event => onClick({ autoplay: true, event })}>{recordingIcon}</div>}
                            <div className="col-auto ms-2 btn-link cursor-pointer" onClick={event => onClick({ event })}>{transcriptIcon}</div>
                          </div>
                          {r.subject_contacts?.[0]?.outcomes?.length > 0 && (
                            <div className="">
                              {(r.subject_contacts?.[0]?.outcomes || []).map((outcome, i) => <span className={`col-auto mt-1 me-1 p-1 small badge ${outcome_color(outcome)}`} key={i}>{outcome}</span>)}
                            </div>
                          )}
                        </>
                      ) : dash}
                    </td>

                    {/* Next Touchpoint */}
                    <td
                      className="text-start align-middle py-2 px-3 text-break"
                      style={textCellStyle}
                      title={String(r.next_touchpoint || "")}
                    >
                      {r.next_touchpoint || dash}
                    </td>

                    {/* Lead Source */}
                    <td
                      className="text-start align-middle py-2 ps-3 text-break"
                      style={textCellStyle}
                      title={r.recruitment_source_name || ""}
                    >
                      {r.recruitment_source_name || dash}
                    </td>

                    {/* Study */}
                    <td
                      className="text-start align-middle py-2 ps-3 text-break"
                      style={textCellStyle}
                      title={r.current_study_name || ""}
                    >
                      {r.current_study_name || dash}
                    </td>

                    {/* Lead Created */}
                    <td
                      className="text-center align-middle py-2 text-nowrap"
                      style={textCellStyle}
                      title={dt(r.lead_created_at).long || ''}
                    >
                      {dt(r.lead_created_at).medium || dash}
                    </td>

                    {/* Updated At + Actions  */}
                    <td className="align-middle py-2 ps-3 text-break" style={lastCellStyle}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">
                          {dt(r.updated_at).relative || dash}
                        </span>

                        <div className="d-flex align-items-center">
                          <Link style={{ color: "var(--bs-body-color)" }} to={{ pathname: path + r.id, search: location.search }}>
                            <FontAwesomeIcon
                              className="cursor-pointer me-3"
                              icon={icons.faPenToSquare}
                              title="Edit"
                            />
                          </Link>

                          <FontAwesomeIcon
                            className="cursor-pointer text-danger"
                            icon={icons.faTrashCan}
                            onClick={event => { event.stopPropagation(); handleDelete(r); }}
                            title="Delete"
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {loadMoreButton}
        </div>
      </div>

      {sidebarItem && (
        <div className="col-auto ps-4 width_300">
          <div className="position-sticky" style={{maxHeight: '100vh', overflow: 'auto', top: '0px'}}>
            <button aria-label="Close" className="btn-close float-end" onClick={() => setSidebarItem()} type="button"></button>
            {subject_breakout(sidebarItem)}
          </div>
        </div>
      )}
    </div>
    );
  }, [
    loadCount,
    sidebarItem,
    isLoadingMore,
    JSON.stringify(busy),
    rows,
    showCCID,
    showCRID,
    showStatus,
    showStudyStatus,
    selectedIds,
    allSelectedOnPage,
    someSelectedOnPage,
    handleRowCheckboxClick
  ]);

  return (
    <div className="container-fluid px-4">
      {error && <ErrorModal message={error} onClose={() => setError()} title="List Error" />}
      {modal}

      {/* very small modal for entering workflow id */}
      {workflowModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog" onClick={() => setWorkflowModal(null)}>
          <div className="modal-dialog modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content shadow">
              <div className="modal-header">
                <h5 className="modal-title">{workflowModal.title}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setWorkflowModal(null)} />
              </div>
              <div className="modal-body">
                <select
                  className="form-select"
                  onChange={e => workflowModal.setValue(e.target.value)}
                  disabled={workflowOptionsLoading}
                >
                  <option value="">Choose Workflow</option>
                  {workflowOptionsLoading && (
                    <option value="" disabled>Loading…</option>
                  )}
                  {!workflowOptionsLoading && workflowOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setWorkflowModal(null)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={workflowModal.onConfirm}>Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Header isHeader={!showDashboard} isSticky={!showDashboard} path="/app/customers/">
        {/* Header */}
        <div className="row g-0 align-items-center">
          {!showDashboard ? (
            <div className="col">
              {links.length > 0 && <Breadcrumb className="mx-3" links={links} small={true} />}
            </div>
          ) : (
            <>
              <div className="mb-5">
                <Dashboard path={path} />
              </div>
              <div className="col">
                <h4 className="mb-0">
                  {title.label || 'All Subjects'}{' '}
                  <small
                    className="p-1 px-2"
                    style={{
                      backgroundColor: 'var(--ava-purple,)',
                      color: '#fff',
                      borderRadius: 4
                    }}
                  >
                    {title.total}
                  </small>
                </h4>
              </div>
            </>
          )}
          {/* Status */}
          <div className="ms-auto col-auto pe-3">
            <Select
              className="form-control-soft width_340"
              getOptionLabel={e => e.status}
              getOptionValue={e => e.status}
              isMulti
              menuPlacement="top"
              name="statuses"
              onChange={setStatuses}
              onMenuOpen={() => loadDistinctStatuses()}
              options={distinctStatuses}
              placeholder="Statuses filter"
              styles={{
                menu: base => ({
                  ...base,
                  zIndex: 5
                }),
                menuList: base => ({
                  ...base,
                  maxHeight: 250
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  whiteSpace: 'normal'
                })
              }}
              value={statuses}
            />
          </div>
          {/* Outcomes */}
          <div className="ms-auto col-auto pe-3">
            <Select
              className="form-control-soft width_340"
              getOptionLabel={e => e.id}
              getOptionValue={e => e.id}
              isMulti
              menuPlacement="top"
              name="outcomes"
              onChange={selected => setOutcomes(selected.map(e => e.id))}
              options={_outcomes}
              placeholder="Outcomes filter"
              styles={{
                menu: base => ({
                  ...base,
                  zIndex: 5
                }),
                menuList: base => ({
                  ...base,
                  maxHeight: 250
                })
              }}
              value={outcomes?.map(e => ({ id: e }))}
            />
          </div>
          {/* Search Box */}
          <div className="col-auto pe-0 position-relative">
            <input
              autoFocus
              className="form-control width_200"
              defaultValue={search}
              key={searchCount}
              placeholder="Search..."
              onChange={(e) => debounceSearch(e.target.value)}
            />
            <i
              className="bi bi-x position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
              onClick={() => {
                setSearch("");
                incSearchCount();
              }}
              role="button"
            ></i>
          </div>

          {/* Bulk actions dropdown (React-controlled; no Bootstrap JS required) */}
          <div
            className="col-auto"
            style={{ marginLeft: "20px" }}
            ref={bulkMenuRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dropdown">
              <button
                className="btn btn-outline-secondary dropdown-toggle"
                type="button"
                aria-expanded={bulkMenuOpen ? "true" : "false"}
                disabled={selectedCount === 0}
                title={selectedCount ? `${selectedCount} selected` : 'Select rows to enable'}
                onClick={(e) => {
                  e.stopPropagation();
                  setBulkMenuOpen((p) => !p);
                }}
              >
                Bulk Actions{selectedCount ? ` (${selectedCount})` : ''}
              </button>

              {bulkMenuOpen && (
                <ul className="dropdown-menu dropdown-menu-end show">
                  <li>
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={() => {
                        setBulkMenuOpen(false);
                        let workflowValue = String(workflow_id || '');
                        setWorkflowModal({
                          title: 'Add Workflow',
                          getValue: () => workflowValue,
                          setValue: (v) => { workflowValue = v; },
                          onConfirm: () => {
                            setWorkflowModal(null);
                            console.log('workflowValue', workflowValue);
                            const wid = parseInt(workflowValue, 10);
                            if (!wid) return alert('Invalid workflow id');
                            handleBulkAddWorkflow(wid);
                          }
                        });
                      }}
                    >
                      Add Workflow
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>

          {/* Create New Study Button */}
          {showDashboard && (
            <div className="col-auto">
              <Link to="/app/pipeline/subjects/new">
                <button
                  className="btn btn-primary"
                  style={{
                    backgroundColor: "#2E2868",
                    borderColor: "#2E2868",
                    marginLeft: "20px",
                  }}
                >
                  Create New Subject
                </button>
              </Link>
            </div>
          )}
        </div>
        <hr />
      </Header>

      {rows?.length ? (
        <AnimatePresence initial={true}>
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1.0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            {table}
          </motion.div>
        </AnimatePresence>
      ) : undefined}

    </div>
  );
};
